import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router';
import { useAuth } from '../context/AuthContext';
import {
  Alert,
  Badge,
  Loader,
  Button,
  Title,
  Body,
  Icon,
  DescriptionList,
} from '../components/kern';

interface Feedback {
  id: number;
  message: string;
  sender_email: string | null;
  rating: number;
  status: string;
  url: string;
  user_agent: string;
  browser_info: string;
  screenshot: string | null;
  project_id: number;
  project_name: string;
  gitlab_issue_url: string | null;
  created_at: string;
  updated_at: string;
}

export function FeedbackDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { token, isAdmin } = useAuth();
  const navigate = useNavigate();

  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [screenshotUrl, setScreenshotUrl] = useState<string | null>(null);

  const fetchFeedback = useCallback(async () => {
    try {
      const response = await fetch(`/api/v1/feedbacks/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Feedback nicht gefunden');
        }
        throw new Error('Fehler beim Laden des Feedbacks');
      }

      const data = await response.json();
      setFeedback(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten');
    } finally {
      setIsLoading(false);
    }
  }, [id, token]);

  useEffect(() => {
    fetchFeedback();
  }, [fetchFeedback]);

  // Load screenshot with auth token
  useEffect(() => {
    if (!feedback?.screenshot) {
      setScreenshotUrl(null);
      return;
    }

    let cancelled = false;
    let objectUrl: string | null = null;

    const loadScreenshot = async () => {
      try {
        const response = await fetch(`/api/v1/feedbacks/${feedback.id}/screenshot`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Screenshot konnte nicht geladen werden');
        }

        const blob = await response.blob();
        if (!cancelled) {
          objectUrl = URL.createObjectURL(blob);
          setScreenshotUrl(objectUrl);
        }
      } catch (err) {
        console.error('Screenshot load error:', err);
        if (!cancelled) {
          setScreenshotUrl(null);
        }
      }
    };

    loadScreenshot();

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [feedback?.id, feedback?.screenshot, token]);

  const handleStatusChange = async (newStatus: string) => {
    if (!feedback) return;
    setIsUpdating(true);

    try {
      const response = await fetch(`/api/v1/feedbacks/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (!response.ok) {
        throw new Error('Fehler beim Aktualisieren des Status');
      }

      setFeedback({ ...feedback, status: newStatus });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Möchtest du dieses Feedback wirklich löschen?')) return;

    try {
      const response = await fetch(`/api/v1/feedbacks/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Fehler beim Löschen des Feedbacks');
      }

      navigate('/feedbacks');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<
      string,
      { variant: 'info' | 'warning' | 'success' | 'danger'; label: string }
    > = {
      new: { variant: 'info', label: 'Neu' },
      pending: { variant: 'warning', label: 'In Bearbeitung' },
      resolved: { variant: 'success', label: 'Erledigt' },
      rejected: { variant: 'danger', label: 'Abgelehnt' },
    };
    const { variant, label } = statusMap[status] || statusMap.new;
    return (
      <Badge variant={variant} icon>
        {label}
      </Badge>
    );
  };

  const renderStars = (rating: number) => {
    return (
      <span className="rating-stars rating-stars--large">
        {Array.from({ length: 5 }, (_, i) => (
          <span key={i} className={`star star--large ${i < rating ? 'star--filled' : ''}`}>
            ★
          </span>
        ))}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <Loader />
        <Body>Lade Feedback...</Body>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <Alert variant="danger" title="Fehler">
          {error}
        </Alert>
        <Link to="/feedbacks" className="kern-link">
          <Icon name="arrow-back" /> Zurück zur Übersicht
        </Link>
      </div>
    );
  }

  if (!feedback) {
    return (
      <div className="error-container">
        <Body>Feedback nicht gefunden.</Body>
        <Link to="/feedbacks" className="kern-link">
          <Icon name="arrow-back" /> Zurück zur Übersicht
        </Link>
      </div>
    );
  }

  return (
    <div className="feedback-detail-page">
      <header className="page-header">
        <div className="page-header-top">
          <Link to="/feedbacks" className="kern-link">
            <Icon name="arrow-back" /> Zurück
          </Link>
        </div>
        <div className="page-header-main">
          <div className="page-header-left">
            <Title size="large" as="h1">
              Feedback #{feedback.id}
            </Title>
            {getStatusBadge(feedback.status)}
          </div>
          <div className="page-header-actions">
            {isAdmin && (
              <Button variant="secondary" onClick={handleDelete} icon="delete">
                Löschen
              </Button>
            )}
          </div>
        </div>
      </header>

      <div className="kern-container detail-grid">
        <div className="kern-row detail-grid-row">
          <div className="kern-col-xl-6 kern-col-md-12">
            <article className="kern-card detail-card message-card">
              <div className="kern-card__container">
                <header className="kern-card__header">
                  <div className="card-header-row">
                    <Title as="h2">Nachricht</Title>
                    <div className="rating-display">{renderStars(feedback.rating)}</div>
                  </div>
                </header>
                <section className="kern-card__body">
                  <Body className="feedback-message">{feedback.message}</Body>
                </section>
              </div>
            </article>
          </div>

          <div className="kern-col-xl-6 kern-col-md-12">
            <article className="kern-card detail-card details-card">
              <div className="kern-card__container">
                <header className="kern-card__header">
                  <Title as="h2">Details</Title>
                </header>
                <section className="kern-card__body">
                  <DescriptionList
                    items={[
                      {
                        key: 'Absender E-Mail',
                        value: feedback.sender_email ? (
                          <a href={`mailto:${feedback.sender_email}`} className="kern-link">
                            {feedback.sender_email} <Icon name="mail" size="small" />
                          </a>
                        ) : (
                          <Body muted>Keine E-Mail-Adresse angegeben</Body>
                        ),
                      },
                      {
                        key: 'URL',
                        value: (
                          <a
                            href={feedback.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="kern-link"
                          >
                            {feedback.url} <Icon name="open-in-new" size="small" />
                          </a>
                        ),
                      },
                      {
                        key: 'Projekt',
                        value: feedback.project_name || '-',
                      },
                      {
                        key: 'Erstellt am',
                        value: formatDate(feedback.created_at),
                      },
                      {
                        key: 'Aktualisiert am',
                        value: formatDate(feedback.updated_at),
                      },
                      ...(feedback.gitlab_issue_url
                        ? [
                            {
                              key: 'GitLab Issue',
                              value: (
                                <a
                                  href={feedback.gitlab_issue_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="kern-link"
                                >
                                  Issue öffnen <Icon name="open-in-new" size="small" />
                                </a>
                              ),
                            },
                          ]
                        : []),
                    ]}
                  />
                </section>
              </div>
            </article>
          </div>
        </div>

        <div className="kern-row detail-grid-row">
          {isAdmin && (
            <div className="kern-col-xl-6 kern-col-md-12">
              <article className="kern-card detail-card">
                <div className="kern-card__container">
                  <header className="kern-card__header">
                    <Title as="h2">Status ändern</Title>
                  </header>
                  <section className="kern-card__body">
                    <div className="status-buttons">
                      <Button
                        variant={feedback.status === 'new' ? 'primary' : 'secondary'}
                        onClick={() => handleStatusChange('new')}
                        disabled={isUpdating || feedback.status === 'new'}
                        icon="mail"
                      >
                        Neu
                      </Button>
                      <Button
                        variant={feedback.status === 'pending' ? 'primary' : 'secondary'}
                        onClick={() => handleStatusChange('pending')}
                        disabled={isUpdating || feedback.status === 'pending'}
                        icon="autorenew"
                      >
                        In Bearbeitung
                      </Button>
                      <Button
                        variant={feedback.status === 'resolved' ? 'primary' : 'secondary'}
                        onClick={() => handleStatusChange('resolved')}
                        disabled={isUpdating || feedback.status === 'resolved'}
                        icon="check"
                      >
                        Erledigt
                      </Button>
                      <Button
                        variant={feedback.status === 'rejected' ? 'primary' : 'secondary'}
                        onClick={() => handleStatusChange('rejected')}
                        disabled={isUpdating || feedback.status === 'rejected'}
                        icon="close"
                      >
                        Abgelehnt
                      </Button>
                    </div>
                  </section>
                </div>
              </article>
            </div>
          )}

          <div className={`kern-col-xl-${isAdmin ? '6' : '12'} kern-col-md-12`}>
            <article className="kern-card detail-card">
              <div className="kern-card__container">
                <header className="kern-card__header">
                  <Title as="h2">Browser-Info</Title>
                </header>
                <section className="kern-card__body">
                  <DescriptionList
                    items={[
                      {
                        key: 'User-Agent',
                        value: (
                          <Body size="small" className="user-agent">
                            {feedback.user_agent || '-'}
                          </Body>
                        ),
                      },
                      ...(feedback.browser_info
                        ? [
                            {
                              key: 'Browser-Details',
                              value: (
                                <pre className="browser-info">
                                  {JSON.stringify(JSON.parse(feedback.browser_info), null, 2)}
                                </pre>
                              ),
                            },
                          ]
                        : []),
                    ]}
                  />
                </section>
              </div>
            </article>
          </div>
        </div>

        {feedback.screenshot && screenshotUrl && (
          <div className="kern-row detail-grid-row">
            <div className="kern-col-12">
              <article className="kern-card detail-card">
                <div className="kern-card__container">
                  <header className="kern-card__header">
                    <Title as="h2">Screenshot</Title>
                  </header>
                  <section className="kern-card__body">
                    <div className="screenshot-container">
                      <img
                        src={screenshotUrl}
                        alt="Screenshot"
                        className="screenshot-image"
                        onClick={() => window.open(screenshotUrl, '_blank')}
                      />
                    </div>
                  </section>
                </div>
              </article>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
